import React, { useEffect } from 'react';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import { FormattedMessage, injectIntl } from '../../util/reactIntl';
import * as validators from '../../util/validators';
import { Form, FieldTextInput, FieldPhoneNumberInput } from '../../components';

import css from './CustomerSignupForms.css';

const CustomerSignupStep1Form = props => {
  const { onSubmit, initialValues, intl } = props;

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }
  }, []);

  // First Name
  const firstNameLabel = intl.formatMessage({ id: 'SignupForm.firstNameLabel' });
  const firstNamePlaceholder = intl.formatMessage({ id: 'SignupForm.firstNamePlaceholder' });
  const firstNameRequired = validators.required(
    intl.formatMessage({ id: 'SignupForm.firstNameRequired' })
  );

  // Last Name
  const lastNameLabel = intl.formatMessage({ id: 'SignupForm.lastNameLabel' });
  const lastNamePlaceholder = intl.formatMessage({ id: 'SignupForm.lastNamePlaceholder' });
  const lastNameRequired = validators.required(
    intl.formatMessage({ id: 'SignupForm.lastNameRequired' })
  );

  // Name validation - shared
  const nameTooShortMessage = intl.formatMessage(
    { id: 'SignupForm.nameTooShort' },
    { minLength: validators.NAME_MIN_LENGTH }
  );
  const nameRestrictedMessage = intl.formatMessage({ id: 'SignupForm.nameRestricted' });
  const nameInvalidCharactersMessage = intl.formatMessage({
    id: 'SignupForm.nameInvalidCharacters',
  });
  const nameMinLength = validators.minLength(nameTooShortMessage, validators.NAME_MIN_LENGTH);
  const nameNotRestricted = validators.nameRestricted(
    nameRestrictedMessage,
    validators.RESTRICTED_NAME_TERMS
  );
  const nameCharsValid = validators.validName(nameInvalidCharactersMessage);

  // Email
  const emailLabel = intl.formatMessage({ id: 'SignupForm.emailLabel' });
  const emailPlaceholder = intl.formatMessage({ id: 'SignupForm.emailPlaceholder' });
  const emailRequired = validators.required(intl.formatMessage({ id: 'SignupForm.emailRequired' }));
  const emailValid = validators.emailFormatValid(
    intl.formatMessage({ id: 'SignupForm.emailInvalid' })
  );
  const emailHelperText = intl.formatMessage({ id: 'SignupForm.emailHelperText' });

  // Phone number
  const phoneNumberLabel = intl.formatMessage({ id: 'SignupForm.phoneNumberLabel' });
  const phoneNumberRequired = validators.phoneNumberRequired(
    intl.formatMessage({ id: 'SignupForm.phoneNumberRequired' })
  );
  const phoneNumberHelperText = intl.formatMessage({ id: 'SignupForm.phoneNumberHelperText' });

  // Password
  const passwordLabel = intl.formatMessage({ id: 'SignupForm.passwordLabel' });
  const passwordPlaceholder = intl.formatMessage({ id: 'SignupForm.passwordPlaceholder' });
  const passwordValidators = validators.composeValidators(
    validators.requiredStringNoTrim(intl.formatMessage({ id: 'SignupForm.passwordRequired' })),
    validators.minLength(
      intl.formatMessage(
        { id: 'SignupForm.passwordTooShort' },
        { minLength: validators.PASSWORD_MIN_LENGTH }
      ),
      validators.PASSWORD_MIN_LENGTH
    ),
    validators.maxLength(
      intl.formatMessage(
        { id: 'SignupForm.passwordTooLong' },
        { maxLength: validators.PASSWORD_MAX_LENGTH }
      ),
      validators.PASSWORD_MAX_LENGTH
    )
  );

  return (
    <FinalForm
      initialValues={{
        fname: initialValues.fname || '',
        lname: initialValues.lname || '',
        email: initialValues.email || '',
        password: initialValues.password || '',
        phoneNumber: initialValues.phoneNumber || '',
      }}
      onSubmit={values => {
        onSubmit(values);
      }}
      render={fieldRenderProps => {
        const { handleSubmit, invalid } = fieldRenderProps;

        return (
          <Form className={css.root} onSubmit={handleSubmit}>
            <div className={css.formFields}>
              {/* First Name + Last Name */}
              <div className={css.nameRow}>
                <FieldTextInput
                  rootClassName={css.field}
                  inputRootClass={css.customInput}
                  type="text"
                  id="fname"
                  name="fname"
                  autoComplete="given-name"
                  label={firstNameLabel}
                  placeholder={firstNamePlaceholder}
                  validate={validators.composeValidators(
                    firstNameRequired,
                    nameMinLength,
                    nameNotRestricted,
                    nameCharsValid
                  )}
                  showSpacer={false}
                />
                <FieldTextInput
                  rootClassName={css.field}
                  inputRootClass={css.customInput}
                  type="text"
                  id="lname"
                  name="lname"
                  autoComplete="family-name"
                  label={lastNameLabel}
                  placeholder={lastNamePlaceholder}
                  validate={validators.composeValidators(
                    lastNameRequired,
                    nameMinLength,
                    nameNotRestricted,
                    nameCharsValid
                  )}
                  showSpacer={false}
                />
              </div>

              {/* Email */}
              <FieldTextInput
                rootClassName={css.field}
                inputRootClass={css.customInput}
                type="email"
                id="email"
                name="email"
                autoComplete="email"
                label={emailLabel}
                placeholder={emailPlaceholder}
                validate={validators.composeValidators(emailRequired, emailValid)}
                showSpacer={false}
                infoText={emailHelperText}
              />

              {/* Phone number */}
              <div className={css.phoneField}>
                <FieldPhoneNumberInput
                  type="text"
                  id="phoneNumber"
                  name="phoneNumber"
                  label={phoneNumberLabel}
                  validate={phoneNumberRequired}
                  infoText={phoneNumberHelperText}
                />
              </div>

              {/* Password */}
              <FieldTextInput
                rootClassName={css.field}
                inputRootClass={css.customInput}
                type="password"
                id="password"
                name="password"
                autoComplete="new-password"
                label={passwordLabel}
                placeholder={passwordPlaceholder}
                validate={passwordValidators}
                showSpacer={false}
              />
            </div>

            <div className={css.nextButtonWrapper}>
              <button type="submit" className={css.submitButton} disabled={invalid}>
                <FormattedMessage id="CustomerSignupForms.nextButton" />
              </button>
            </div>
          </Form>
        );
      }}
    />
  );
};

export default compose(injectIntl)(CustomerSignupStep1Form);
